import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../ui/Button';
import { User, Bell, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const EditProfileSection = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    full_name: '',
    company_name: '',
    phone_number: '',
    email_notifications: {
      job_started: false,
      job_completed: true,
      job_failed: true,
      marketing: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name !== 'N/A' ? user.full_name : '',
        company_name: user.company_name !== 'N/A' ? user.company_name : '',
        phone_number: user.phone_number !== 'N/A' ? user.phone_number : '',
        email_notifications: user.email_notifications || {
          job_started: false,
          job_completed: true,
          job_failed: true,
          marketing: false
        }
      });
    }
  }, [user]);

  const handleInputChange = (field, value) => {
    if (field.startsWith('email_notifications.')) {
      const k = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        email_notifications: { ...prev.email_notifications, [k]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = () => {
    if (!formData.full_name.trim()) {
      setError('Full name is required');
      return false;
    }
    if (formData.full_name.trim().length < 2) {
      setError('Full name must be at least 2 characters long');
      return false;
    }
    if (formData.company_name.trim().length > 0 && formData.company_name.trim().length < 2) {
      setError('Company name must be at least 2 characters long');
      return false;
    }
    if (formData.phone_number.trim().length > 0 && formData.phone_number.trim().length < 10) {
      setError('Phone number must be at least 10 characters long');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_BASE_URL}/api/user/profile`, {
        full_name: formData.full_name.trim(),
        company_name: formData.company_name.trim() || 'N/A',
        phone_number: formData.phone_number.trim() || 'N/A',
        email_notifications: formData.email_notifications
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setSuccess('Profile updated successfully!');
      if (updateUser) updateUser(response.data.user);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: '720px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
        <User size={18} color="#6b7280" />
        <h2 style={{ margin: 0, fontSize: '18px' }}>Edit Profile</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: 6 }}>Full Name *</label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => handleInputChange('full_name', e.target.value)}
              required
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: 6 }}>Company Name</label>
            <input
              type="text"
              value={formData.company_name}
              onChange={(e) => handleInputChange('company_name', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: 6 }}>Phone Number</label>
            <input
              type="tel"
              value={formData.phone_number}
              onChange={(e) => handleInputChange('phone_number', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 14 }}
            />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '8px 0' }}>
              <Bell size={18} color="#6b7280" />
              <h3 style={{ margin: 0, fontSize: '16px' }}>Email Notifications</h3>
            </div>
            {[
              { key: 'job_started', label: 'Job Started', desc: 'Get notified when your jobs begin' },
              { key: 'job_completed', label: 'Job Completed', desc: 'Get notified when jobs complete successfully' },
              { key: 'job_failed', label: 'Job Failed', desc: 'Get notified when jobs encounter errors' },
              { key: 'marketing', label: 'Marketing Updates', desc: 'Receive product updates and tips' }
            ].map(({ key, label, desc }) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0' }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{label}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{desc}</div>
                </div>
                <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                  <input type="checkbox" checked={formData.email_notifications[key]} onChange={(e) => handleInputChange(`email_notifications.${key}`, e.target.checked)} style={{ display: 'none' }} />
                  <div style={{ position: 'absolute', inset: 0, background: formData.email_notifications[key] ? '#8b5cf6' : 'var(--color-border-strong)', borderRadius: 12 }}>
                    <div style={{ position: 'absolute', top: 2, left: formData.email_notifications[key] ? 22 : 2, width: 20, height: 20, background: '#fff', borderRadius: '50%', transition: 'left 0.2s ease' }} />
                  </div>
                </label>
              </div>
            ))}
          </div>

          {error && (
            <div style={{ background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger-border)', color: 'var(--color-danger-hover)', padding: 12, borderRadius: 6, fontSize: 14 }}>{error}</div>
          )}
          {success && (
            <div style={{ background: 'var(--color-success-bg)', border: '1px solid var(--color-success-border)', color: 'var(--color-success-fg)', padding: 12, borderRadius: 6, fontSize: 14, display: 'flex', gap: 8, alignItems: 'center' }}>
              <Check size={16} /> {success}
            </div>
          )}
          <Button type="submit" loading={loading} disabled={loading} size="sm" icon={<Check size={14} /> }>
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditProfileSection;